/**
 * CurrencySwitcher — small dropdown to change display currency.
 *
 * Reads from DisplayCurrencyContext. Pure UI; no local state beyond what
 * the dropdown library needs.
 *
 * Visual note: when the rates are static fallback (cachedAt === null), we
 * show a subtle "approx" indicator next to the dropdown so the user knows
 * their conversion isn't real-time. Honesty over polish.
 */
import React from "react";
import { ChevronDown, Coins, AlertCircle } from "lucide-react";
import { useDisplayCurrency } from "@/context/DisplayCurrencyContext";
import { format as formatDate } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { currencySymbol } from "@/context/DisplayCurrencyContext";
import { currencyInfo } from "@/lib/currency";

export const CurrencySwitcher: React.FC = () => {
  const {
    displayCurrency,
    setDisplayCurrency,
    availableCurrencies,
    ratesCachedAt,
    fxUnavailable,
  } = useDisplayCurrency();

  const ratesLabel = ratesCachedAt
    ? `Rates as of ${formatDate(new Date(ratesCachedAt), "MMM d, h:mm a")}`
    : "Approximate rates";

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Change display currency"
          >
            <Coins className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="tabular-nums">
              {currencySymbol(displayCurrency)} {displayCurrency}
            </span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border mb-1">
            Display currency
          </div>
          {availableCurrencies.map((c) => (
            <DropdownMenuItem
              key={c}
              onClick={() => setDisplayCurrency(c)}
              className={`flex items-center justify-between cursor-pointer ${
                c === displayCurrency ? "bg-primary/5 font-medium" : ""
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="text-base tabular-nums">
                  {currencySymbol(c)}
                </span>
                <span className="text-sm">{currencyInfo(c).name}</span>
              </span>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {c}
              </span>
            </DropdownMenuItem>
          ))}
          <div className="px-2 py-1.5 mt-1 border-t border-border text-[10px] text-muted-foreground flex items-center gap-1">
            {fxUnavailable && (
              <AlertCircle className="h-3 w-3 text-amber-500" />
            )}
            {ratesLabel}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};